/**
 * Treści po polsku. Nazwy trybów są zgodne z katalogiem `pl.json`.
 *
 * Liczebniki są zapisane w formie « etykieta: {n} » albo z liczbą przed
 * rzeczownikiem w mianowniku tam, gdzie to naturalne: polski odmienia
 * rzeczownik po liczbie, a te teksty nie przechodzą przez żaden mechanizm
 * liczby mnogiej.
 */
import type { LanguageContent } from '../registry.js';

export const PL: LanguageContent = {
  home: {
    intro: [
      'Retusch to gra rysunkowa online dla 2 do 10 osób, grywalna w przeglądarce, bez instalacji i bez konta. W każdej rundzie dostajesz obraz, z którego wymazano jedno pole: odmaluj je tak, aby zniknęło w dziele. Potem role się odwracają i trzeba wytropić retusze pozostałych.',
      'Większość gier rysunkowych nagradza tego, kogo najszybciej rozpoznano. Retusch wymaga czegoś przeciwnego: wygrywa się wtedy, gdy nikt cię nie zauważy.',
    ],
    steps: [
      {
        title: 'Obraz z dziurą',
        text: 'Dostajesz ilustrację, z której wymazano jedno pole.',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: 'Odmalowujesz je',
        text: 'Rysujesz pole na nowo, tak aby wtopiło się w dzieło.',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: 'Polowanie',
        text: 'Obraz wraca z retuszami wszystkich graczy: znajdź je przed innymi.',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: 'Odsłona',
        text: 'Każda kryjówka zostaje ujawniona i spadają punkty.',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: 'Wspólne polowanie',
        text: 'Wszyscy malują na tym samym obrazie, każdy w swoim polu. Potem polowanie otwiera się dla wszystkich naraz: retusz można znaleźć tylko raz, robi to ten, kto dostrzeże go pierwszy. To tryb domyślny, ten, w którym cały czas coś się dzieje.',
      },
      {
        name: 'Stream',
        text: 'Gospodarz nie rysuje: szuka sam, podczas gdy wszyscy pozostali chowają się w jego obrazie. Pomyślany dla transmisji na żywo, gdzie polowanie staje się widowiskiem. Za darmo na zawsze.',
      },
      {
        name: 'Każdy ma swój obraz',
        text: 'Jedno dzieło na gracza. Szukasz retuszu tylko jednej osoby, jednym kliknięciem. Najprostsza zasada w grze i zarazem najbardziej napięta.',
      },
      {
        name: 'Rysuje jedna osoba',
        text: 'Jedna runda, jeden rysownik, wszyscy pozostali w pogoni. Rola krąży, a rysownik patrzy, jak jego kryjówka upada albo się broni.',
      },
      {
        name: 'Battle royale',
        text: 'To samo polowanie, ale pierwszy odkryty retusz eliminuje swojego autora. Gra się do ostatniego, nie licząc ani jednego punktu.',
      },
      {
        name: 'Konkurs',
        text: 'Nikt nie szuka. Wszyscy odmalowują to samo pole tego samego obrazu, a potem każdy głosuje na retusz, który najbardziej mu się podoba. Otrzymany głos to jeden punkt.',
      },
    ],
    faq: [
      {
        question: 'Ilu graczy potrzeba?',
        answer:
          'Od 2 do 10. Jeśli jesteś sam, wolne miejsce zajmuje automatyczny przeciwnik, żeby partia zaczęła się od razu. Od czterech osób wspólne polowanie nabiera pełnego rozmachu.',
      },
      {
        question: 'Czy gra jest darmowa?',
        answer:
          'Tak. Szybka partia, wspólne polowanie i tryb stream są darmowe, bez limitu czasu i liczby rozgrywek. Pozostałe tryby na razie również.',
      },
      {
        question: 'Czy trzeba założyć konto?',
        answer:
          'Nie. Wpisujesz pseudonim, klikasz, grasz. Konto służy wyłącznie do zapamiętania pseudonimu i awatara między partiami: pozostaje opcjonalne.',
      },
      {
        question: 'Czy działa na telefonie?',
        answer:
          'Tak, interfejs dostosowuje się do dotyku: zwinięty pasek narzędzi, lupa pod palcem, podwójne stuknięcie do przybliżenia. Mimo to myszą i na dużym ekranie rysuje się dokładniej.',
      },
      {
        question: 'Jak zaprosić znajomych?',
        answer:
          'Utwórz pokój, skopiuj link z zaproszeniem i wklej go, gdzie chcesz. Znajomi trafiają prosto do stołu, bez konta i bez instalowania czegokolwiek.',
      },
      {
        question: 'Czym jest «retusz»?',
        answer:
          'To, co malujesz w wymazanym polu. Udany uchodzi za fragment oryginalnego obrazu i nikt go nie znajduje. Nieudany rzuca się w oczy.',
      },
    ],
    headings: {
      intro: 'Gra rysunkowa, w której trzeba zniknąć',
      steps: 'Jak się gra',
      modes: 'Sześć sposobów gry',
      faq: 'Częste pytania',
      more: 'Czytaj dalej',
    },
    links: {
      rules: 'Przeczytaj pełne zasady i punktację',
      modes: 'Porównaj tryby szczegółowo',
    },
  },

  howToPlay: {
    title: 'Jak grać w Retusch',
    summary:
      'Pełne zasady Retusch: przebieg rundy, punktacja po obu stronach, narzędzia do rysowania oraz porady, jak się ukryć i jak szukać.',
    sections: [
      {
        heading: 'Pomysł w trzech zdaniach',
        blocks: [
          'Dostajesz obraz z wymazanym polem i odmalowujesz je tak, aby pozostało niezauważone.',
          'Potem wszyscy oglądają wyretuszowane obrazy i szukają odmalowanych pól.',
          'Punktujesz, znajdując szybko, i punktujesz również tak długo, jak długo broni się twoja własna kryjówka.',
        ],
      },
      {
        heading: 'Przebieg rundy',
        blocks: [
          'Partia składa się z takich samych rund, domyślnie pięciu. Każda z nich ma trzy momenty.',
          {
            list: [
              'Rysowanie. Dostajesz swój obraz i swoje pole, a na odmalowanie masz domyślnie 2 minuty. Przycisk Gotowe oznacza oddanie pracy: gdy oddadzą wszyscy, runda idzie dalej, nie czekając na zegar.',
              'Polowanie. Obraz wraca z retuszami, a ty masz domyślnie 30 sekund, żeby je wypatrzyć. Kliknięcie w odmalowane pole odsłania je całemu stołowi.',
              'Odsłona. Wszystkie kryjówki dostają ramkę i nazwisko autora, także te, których nikt nie zauważył. Suwak pozwala porównać obraz wyretuszowany z oryginałem.',
            ],
          },
        ],
      },
      {
        heading: 'Punktacja',
        blocks: [
          'Dwa liczniki biegną jednocześnie i działają przeciwko sobie: kto szuka, chce być szybki, kto rysował, chce, by jego pole się obroniło.',
          {
            list: [
              'Po stronie szukającego: 1000 punktów, jeśli znajdziesz w ciągu pierwszych 3 sekund, potem równomierny spadek aż do 100 punktów na koniec fazy. Chybienie albo brak znaleziska nie daje nic.',
              'Po stronie rysownika: tylko 100 punktów, jeśli twoje pole padnie natychmiast, potem równomierny wzrost aż do 800 punktów, jeśli obroni się do końca. Retusz, którego nikt nie znajdzie, jest wart maksimum, czyli 800 punktów.',
            ],
          },
          'Rysownik dostaje zapłatę tylko raz, przy odsłonie, i według najszybszego szukającego. Nie ma więc znaczenia, czy szuka cię dwoje ludzi, czy ośmioro: liczy się tylko ten, kto znajdzie cię pierwszy.',
          'Punktacja jest taka sama niezależnie od ustawionego czasu fazy. Wydłużenie polowania nie przesuwa obu granic, a jedynie nachylenie między nimi.',
          'Jedna pomyłka na rundę. Pierwsze kliknięcie, które nie trafia w żaden retusz, kończy twoje polowanie: bez tej zasady przeczesywałoby się obraz na chybił trafił, aż coś się trafi. Dwa ruchy nic nie kosztują: kliknięcie we własne pole i kliknięcie w retusz już znaleziony.',
        ],
      },
      {
        heading: 'Narzędzia do rysowania',
        blocks: [
          {
            list: [
              'Pędzel (B): trzy szybkie rozmiary oraz suwak do dokładnego rozmiaru. Skróty 1, 2 i 3 wybierają te trzy rozmiary.',
              'Wiadro (P): wypełnia za jednym razem jednolitą powierzchnię, nigdy nie wychodząc poza twoje pole.',
              'Kroplomierz (I): pobiera kolor w dowolnym miejscu obrazu. Przytrzymanie Alt przełącza na niego na jedno kliknięcie i potem oddaje poprzednie narzędzie.',
              'Rączka (H): przesuwa widok. Poza twoim polem pędzel i wiadro robią to samo, więc prawie nigdy nie musisz zmieniać narzędzia.',
              'Cofnij (Ctrl+Z lub Cmd+Z): wraca pociągnięcie po pociągnięciu, do trzydziestu razy. Ponowienia nie ma.',
              'Kolor tła na końcu palety: to kolor początkowy twojego pola. Malowanie nim jest równoznaczne z wymazywaniem, z tą różnicą, że to pociągnięcie jak każde inne, które można cofnąć i zamalować. Dlatego nie ma gumki.',
            ],
          },
        ],
      },
      {
        heading: 'Pięć porad, jak się ukryć',
        blocks: [
          {
            list: [
              'Pobieraj kolory, nie zgaduj ich. Kroplomierz daje dokładny kolor otoczenia: ledwie przesunięta plama widoczna jest z daleka, a dokładna staje się niewidzialna.',
              'Przedłużaj to, co przecina twoje pole. Mur, horyzont, gałąź wchodząca z jednej strony musi wyjść z drugiej we właściwym miejscu. Przerwana linia to pierwsza wskazówka, jakiej szuka doświadczony gracz.',
              'Najpierw maluj rogi. To najczęstszy błąd: środek jest dopracowany, cztery rogi zostają w pierwotnym tle, a kwadrat widać gołym okiem, nawet bez patrzenia na rysunek.',
              'Idź od ogółu do szczegółu. Najpierw połóż duże plamy koloru grubym pędzlem, dopiero potem dopracuj. Odwrotnie zjadasz cały czas na jednym rogu, podczas gdy reszta zostaje pusta.',
              'Naśladuj poziom szczegółowości otoczenia. Zbyt czyste pole pośrodku gęstego fragmentu widać tak samo, jak niedbałe pole pośrodku jednolitej plamy.',
            ],
          },
        ],
      },
      {
        heading: 'Cztery porady, jak szukać',
        blocks: [
          {
            list: [
              'Szukaj linii, które się urywają. Krawędź, kabel, brzeg stołu przesunięty o włos: prawie zawsze jest właśnie tam.',
              'Najpierw omiataj, potem przybliżaj. W pełnym widoku źle wtopione pole wygląda jak plama. Powiększenie służy do potwierdzenia, nie do szukania.',
              'Nie ufaj temu, co zbyt gładkie. Idealnie równa powierzchnia pośrodku faktury jest równie podejrzana jak smuga.',
              'Nie klikaj na oślep. Masz prawo tylko do jednej pomyłki: lepiej stracić trzy sekundy na sprawdzenie niż całą rundę.',
            ],
          },
        ],
      },
      {
        heading: 'A zależnie od trybu?',
        blocks: [
          'Wszystko powyższe opisuje wspólne polowanie, tryb domyślny. Pozostałe tryby zmieniają to, kto rysuje, kto szuka i jak się wygrywa: szczegóły każdego z nich znajdziesz na stronie trybów gry.',
        ],
      },
    ],
  },

  modes: {
    title: 'Tryby gry w Retusch',
    summary:
      'Sześć sposobów gry w Retusch: wspólne polowanie, stream, każdy ma swój obraz, rysuje jedna osoba, battle royale i konkurs. Przebieg, zalecana liczba graczy i czas trwania.',
    sections: [
      {
        heading: 'Wspólne polowanie',
        blocks: [
          'Tryb domyślny i ten, od którego warto zacząć.',
          'Wszyscy rysują na tym samym obrazie, każdy w swoim polu. Gdy retusze zostaną oddane, dzieło wraca w całości, a polowanie otwiera się dla wszystkich naraz. Retusz można znaleźć tylko raz, robi to ten, kto dostrzeże go pierwszy: pojawia się wtedy dla całego stołu i wypada z gry. Własnego nigdy się nie znajduje, a kryjówka, której nikt nie zobaczy, daje autorowi maksimum.',
          'Idealne dla dużej grupy: nie ma martwych chwil, wszyscy szukają jednocześnie, a każde znalezisko widać na ekranie każdego.',
          'Zalecana liczba graczy: od 3 do 10.',
        ],
      },
      {
        heading: 'Stream',
        blocks: [
          'Lustrzane odbicie trybu «rysuje jedna osoba», pomyślane dla transmisji na żywo.',
          'Wszyscy gracze rysują na tym samym obrazie, z wyjątkiem gospodarza. Potem gospodarz jako jedyny szuka, a jego widownia patrzy, jak się męczy. Nie zdobywa punktów i nie pojawia się w klasyfikacji: partia toczy się między tymi, którzy się chowają, a on prowadzi polowanie. Może mylić się dowolną liczbę razy, zasada jednej pomyłki go nie dotyczy.',
          'Idealne na wieczór na żywo, i to z konkretnego powodu: skoro gospodarz nie rysuje, widzowie nigdy nie widzą, jak powstaje jego kryjówka. W pozostałych trybach patrzą, jak maluje swoje pole, i polowanie nie ma dla nich żadnej tajemnicy.',
          'Zalecana liczba graczy: od 4 do 10. Za darmo, i tak zostanie.',
        ],
      },
      {
        heading: 'Każdy ma swój obraz',
        blocks: [
          'Każdy gracz dostaje własne dzieło i chowa w nim swój retusz. Podczas polowania otrzymujesz obraz tylko jednej innej osoby i masz jedno kliknięcie, żeby wskazać odmalowane pole. Bez wyścigu i bez konkurencji: tylko ty, obraz i decyzja.',
          'Idealne we dwoje albo wtedy, gdy chcecie spokojnej partii, w której każdy ma czas się przyjrzeć.',
          'Zalecana liczba graczy: od 2 do 10.',
        ],
      },
      {
        heading: 'Rysuje jedna osoba',
        blocks: [
          'Jedna runda, jeden rysownik, wszyscy pozostali w pogoni.',
          'W każdej rundzie obraz dostaje tylko jedna osoba. Pozostali czekają, a potem szukają jej retuszu, każdy oceniany według własnego czasu. Rysownik oceniany jest według najszybszego szukającego i ogląda swoją zamrożoną kryjówkę, gdy inni jej szukają. Rola krąży: ustawienie rund liczy tu pełne kolejki, bo partia jest sprawiedliwa tylko wtedy, gdy każdy rysował tyle samo razy.',
          'Idealne dla małej grupy, która chce się zmierzyć na równych warunkach, rysunek po rysunku.',
          'Zalecana liczba graczy: od 3 do 6, powyżej czekanie robi się długie.',
        ],
      },
      {
        heading: 'Battle royale',
        blocks: [
          'Wspólne polowanie z jedną eliminacją na rundę.',
          'Ten sam wspólny obraz, to samo równoczesne polowanie. Tyle że tu nie liczy się żadnych punktów: jedyne pytanie brzmi, kto zostaje. Gracz, którego retusz odkryto jako pierwszy, opuszcza partię. Pozostali szukają dalej aż do końca rundy, co nikogo już nie eliminuje, ale daje każdemu jego miejsce, pokazane na jego polu. Jeśli nikogo nie znaleziono, nikt nie odpada.',
          'Idealne na zakończenie wieczoru: napięcie rośnie w miarę, jak stół pustoszeje, a ostatnia runda to pojedynek.',
          'Zalecana liczba graczy: od 4 do 10.',
        ],
      },
      {
        heading: 'Konkurs',
        blocks: [
          'Jedyny tryb, w którym nikt nie szuka.',
          'Wszyscy odmalowują to samo pole tego samego obrazu. Potem każdy przegląda retusze pozostałych we własnym tempie i głosuje na ten, który uzna za najlepszy. Otrzymany głos daje autorowi jeden punkt. W czasie głosowania autorzy są anonimowi i nie można głosować na siebie: nazwy i głosy wychodzą na jaw dopiero przy odsłonie.',
          'Idealne, gdy grupa jest różnorodna albo gdy ktoś woli rysować bez presji zegara polowania.',
          'Zalecana liczba graczy: od 3 do 10.',
        ],
      },
    ],
  },
  modeTable: [
    { name: 'Wspólne polowanie', players: 'od 3 do 10', duration: '15 do 20 min' },
    { name: 'Stream', players: 'od 4 do 10', duration: '15 do 20 min' },
    { name: 'Każdy ma swój obraz', players: 'od 2 do 10', duration: '15 do 20 min' },
    { name: 'Rysuje jedna osoba', players: 'od 3 do 6', duration: '20 do 35 min' },
    { name: 'Battle royale', players: 'od 4 do 10', duration: '20 do 30 min' },
    { name: 'Konkurs', players: 'od 3 do 10', duration: '10 do 15 min' },
  ],
  modeTableHeadings: ['Tryb', 'Gracze', 'Czas'],
  premium: {
    heading: 'Tryby darmowe i tryby płatne',
    blocks: [
      'Wspólne polowanie i tryb stream są darmowe na stałe.',
      'Pozostałe cztery staną się płatne przy TWORZENIU partii, i tylko wtedy. Dołączanie zawsze pozostanie darmowe: wystarczy, że jedna osoba przy stole odblokowała grę, aby wszyscy mogli grać we wszystkie tryby, bez konta i bez płacenia.',
      'Odblokowanie będzie jednorazowym zakupem, bez abonamentu, i usunie także reklamy dla konta, które go dokonało. Do tego czasu wszystkie tryby są otwarte dla wszystkich.',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: 'Czym jest Retusch?',
      date: '2026-08-21',
      summary:
        'Gra rysunkowa online dla wielu osób, formą bliska Gartic Phone, ale mechaniką przypominająca Meccha Chameleon: zamiast dać się rozpoznać, próbujesz zniknąć.',
      sections: [
        {
          heading: 'Gra rysunkowa, w której rozpoznanie oznacza przegraną',
          blocks: [
            'Retusch to gra rysunkowa online dla 2 do 10 osób. W każdej rundzie dostajesz ilustrację z wymazanym polem i musisz je odmalować tak, by wtopiło się w dzieło. Potem wszyscy oglądają wyretuszowane obrazy i szukają odmalowanych pól.',
            'To, co ją wyróżnia, mieści się w jednym zdaniu: wygrywa się wtedy, gdy nikt cię nie zauważy.',
          ],
        },
        {
          heading: 'Co łączy ją z Gartic Phone',
          blocks: [
            'Forma jest ta sama: otwierasz link, wpisujesz pseudonim i grasz. Bez konta, bez instalacji, bez klienta do pobrania. Gra działa w przeglądarce, a partia rusza w kilka sekund. Znajomych zaprasza się, wklejając link do rozmowy, i stół się zapełnia.',
            'Rytm też jest znajomy: krótkie rundy, zegar, faza rysowania, a potem faza, w której odkrywa się, co stworzyli inni. A przede wszystkim ta wspólna odsłona na koniec rundy, gdy każdy komentuje to, co widzi. To ta sama przyjemność z gry w grupie.',
            'Podobnie jak w Gartic Phone nie trzeba umieć rysować. Dobrze położony kawałek nieba wygrywa z arcydziełem, które wychodzi poza pole.',
          ],
        },
        {
          heading: 'Co zapożycza z Meccha Chameleon',
          blocks: [
            'W istocie jednak mechanika leży gdzie indziej. W Meccha Chameleon, trójwymiarowej grze na Steam, malujesz własną postać, a potem ustawiasz ją w scenerii, licząc na to, że nikt nie odróżni jej od otoczenia. Zwycięstwo bierze się z podobieństwa i dobrego ustawienia, nie z działania.',
            'Retusch podejmuje ten pomysł i sprowadza go z powrotem do obrazu. Nie wybierasz, gdzie się schować, pole daje ci gra; za to w całości tworzysz to, co się w nim znajdzie. Kryjówka udaje się wtedy, gdy wzrok prześlizguje się po niej i nie zatrzymuje.',
            'I jak w każdej grze o kamuflażu napięcie bierze się z podwójnej roli. Jesteś jednocześnie ukryty i polujesz: gdy przeczesujesz obraz w poszukiwaniu podejrzanej plamy, ktoś przeczesuje twój.',
          ],
        },
        {
          heading: 'Czego nie ma nigdzie indziej',
          blocks: [
            'Punktacja nagradza obie strony przeciwstawnie. Kto szuka, zdobywa tym więcej, im szybciej znajdzie; kto rysował, zdobywa tym więcej, im dłużej broni się jego pole. Retusz, którego nikt nie zauważy, daje autorowi maksimum.',
            'Wynika z tego bardzo szczególny sposób gry: nie chodzi o to, żeby było ładnie, tylko żeby się zgadzało. Pobrać właściwy kolor, przedłużyć linię we właściwym miejscu, nie zostawić pustego rogu. To czynności obserwacji, zanim staną się czynnościami rysowania.',
            'Najprościej zrozumieć to w praktyce: przeczytaj pełne zasady albo uruchom szybką partię, która startuje bez ustawień i bez czekania.',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'Jak stać się niepokonanym w Retusch',
      date: '2026-08-21',
      summary:
        'Skróty klawiszowe, metoda od ogółu do szczegółu i błąd pustych rogów: trzy rzeczy, które dzielą początkującego od gracza, którego już nie sposób znaleźć.',
      sections: [
        {
          heading: 'Naucz się skrótów, a podwoisz swój użyteczny czas',
          blocks: [
            'Dwie minuty rysowania mijają bardzo szybko, gdy po każde narzędzie sięga się myszą. Skróty odzyskują bez trudu dwadzieścia lub trzydzieści sekund na rundę, a to właśnie te sekundy poświęcisz na szczegóły.',
            {
              list: [
                'B na pędzel, P na wiadro, I na kroplomierz, H na rączkę.',
                '1, 2 i 3 na trzy rozmiary pędzla, bez sięgania do paska narzędzi.',
                'Ctrl+Z, a na Macu Cmd+Z, żeby cofać pociągnięcie po pociągnięciu.',
                'Przytrzymanie Alt przełącza na kroplomierz i oddaje poprzednie narzędzie, gdy tylko puścisz. To najbardziej przydatny z nich wszystkich: pobierasz kolor w trakcie pracy, nigdy nie zmieniając narzędzia.',
              ],
            },
            'Uwaga o klawiaturach AZERTY: klawisze 1, 2 i 3 działają bez Shift, mimo że twoja klawiatura wypisuje tam &, é i ". Gra odczytuje położenie klawisza, a nie znak, który on wytwarza.',
          ],
        },
        {
          heading: 'Maluj od ogółu do szczegółu',
          blocks: [
            'Klasyczny błąd początkującego to zacząć od jednego rogu i go dopieszczać. Zegar dobiega końca, trzy czwarte pola wciąż jest tłem, a retusz widać z dziesięciu metrów.',
            'Właściwa metoda jest odwrotna. Zacznij grubym pędzlem albo wiadrem i połóż duże plamy koloru: niebo, ścianę, cień na ziemi. Po dwudziestu sekundach twoje pole ma już właściwy ogólny wygląd i przestaje czytać się jak obcy blok.',
            'Dopiero potem zmniejsz rozmiar i zajmij się przejściami: linią horyzontu, która przecina pole, krawędzią przedmiotu, odcieniem zmieniającym się w połowie wysokości. Jeśli zabraknie czasu, stracisz szczegół, a nie rundę.',
            'Kolory zawsze pobieraj kroplomierzem, zamiast wybierać je z palety. Przybliżony odcień wystarczy, by zdradzić całe pole, a dokładny czyni je niewidzialnym.',
          ],
        },
        {
          heading: 'Nigdy nie zostawiaj pustych rogów',
          blocks: [
            'To wskazówka numer jeden i pierwsza rzecz, jakiej szukają doświadczeni gracze. Pole z dobrze dopracowanym środkiem, ale z czterema rogami pozostawionymi w kolorze tła rysuje na ekranie idealny prostokąt. Nie trzeba nawet patrzeć na rysunek: kształt geometryczny rzuca się w oczy.',
            'Wyrób sobie nawyk, żeby najpierw zająć się obrzeżem, a potem środkiem. Dobrze wtopiona ramka wybaczy niedopracowane wnętrze; odwrotnie nigdy.',
            'Celowo wychodź poza krawędzie. Gra przycina twoje pociągnięcie na granicy pola, więc nic nie ryzykujesz, idąc znacznie dalej, a to jedyny sposób, by mieć pewność, że w rogu nie przetrwał żaden piksel tła.',
          ],
        },
        {
          heading: 'A gdy przychodzi twoja kolej na szukanie',
          blocks: [
            'Najpierw omieć cały obraz bez przybliżania: źle wtopione pole wygląda jak plama, a powiększenie służy tylko do potwierdzenia. Potem podążaj za długimi liniami, tymi, które przecinają obraz: krawędź przesunięta o włos to prawie zawsze retusz.',
            'I pamiętaj, że na rundę masz prawo tylko do jednej pomyłki. Trzy sekundy sprawdzania kosztują mniej niż cała runda.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: 'Gra rysunkowa na wieczór ze znajomymi',
      date: '2026-08-21',
      summary:
        'Dlaczego Retusch dobrze sprawdza się w małej grupie, jak zaprosić, wklejając link, i dlaczego wystarczy jeden gracz, by odblokować płatne tryby dla całego stołu.',
      sections: [
        {
          heading: 'Gra, która nikogo o nic nie prosi',
          blocks: [
            'Największą przeszkodą wieczoru z grami na odległość jest instalacja. Konto do założenia, klient do pobrania, wersja, która się nie zgadza: połowa grupy odpada, zanim zdąży zagrać.',
            'Retusch działa w przeglądarce. Znajomi klikają w link, wpisują pseudonim i siadają do stołu. Nie ma czego instalować ani zakładać, a działa tak samo dobrze na komputerze, jak na telefonie.',
            'Jeszcze coś, co liczy się w grupie: nikt nie zostaje z boku dlatego, że nie umie rysować. Gra nie każe niczego przedstawiać, tylko dopasować pole do tego, co je otacza. Ten, kto rysuje słabo, ale dobrze patrzy, często wygrywa.',
          ],
        },
        {
          heading: 'Zapraszanie w praktyce',
          blocks: [
            'Utwórz pokój z ekranu startowego. Odznacz «pokój publiczny», jeśli chcecie zostać w swoim gronie: zaproszenie linkiem staje się wtedy jedyną drogą wejścia.',
            'Skopiuj link przeznaczonym do tego przyciskiem, a potem wklej go na wasz czat grupowy, kanał głosowy albo komunikator. Zawiera kod pokoju: znajomi lądują prosto przy twoim stole.',
            'Partię możesz ustawiać, gdy ludzie się schodzą: tryb, liczbę rund, czas rysowania, wielkość pola, a nawet kategorie obrazów. Nic nie jest przesądzone, dopóki nie zaczniesz.',
          ],
        },
        {
          heading: 'Jeden płaci, grają wszyscy',
          blocks: [
            'Dwa tryby są darmowe na zawsze: wspólne polowanie, czyli tryb domyślny, oraz tryb stream.',
            'Pozostałe staną się płatne, ale tylko przy TWORZENIU partii. Dołączanie pozostanie darmowe, bez warunków. W praktyce wystarczy, że jedna osoba z grupy odblokowała grę, by cały stół korzystał ze wszystkich trybów, bez konta i bez płacenia.',
            'Odblokowanie to jednorazowy zakup, bez abonamentu.',
          ],
        },
        {
          heading: 'Jaki tryb dla jakiej grupy',
          blocks: [
            'We troje albo we czworo wspólne polowanie w zupełności wystarcza i rozumie się je po jednej rundzie. Od sześciu osób robi się naprawdę dobre: znaleziska następują jedno po drugim, a ekran ani na chwilę nie stoi w miejscu.',
            'Na zakończenie wieczoru spróbujcie battle royale: stół pustoszeje runda po rundzie, a ostatnia to pojedynek. Dla różnorodnej grupy, w której niektórzy wolą rysować spokojnie, konkurs zdejmuje całą presję zegara polowania.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: 'Gra rysunkowa na transmisję na żywo',
      date: '2026-08-21',
      summary:
        'Retusch ma tryb pomyślany dla transmisji na żywo, w którym streamer szuka sam, a jego widownia się chowa. Jak z niego korzystać i które inne tryby sprawdzają się przed publicznością.',
      sections: [
        {
          heading: 'Tryb stworzony właśnie do tego',
          blocks: [
            'Tryb stream odwraca role. Wszyscy gracze rysują na tym samym obrazie z wyjątkiem gospodarza, który nie rysuje wcale. Potem gospodarz jako jedyny szuka, a jego widownia patrzy.',
            'To dokładnie ta sytuacja, jakiej wymaga transmisja, i to z konkretnego powodu: skoro gospodarz nie rysuje, jego własna kryjówka nie istnieje, a czat nie zobaczył niczego, czego nie powinien. Kryjówki pozostałych graczy widownia zna za to doskonale i może patrzeć, jak trzy razy z rzędu przechodzi obok nich.',
            'Na tę okazję napisano dwie zasady. Streamer nie zdobywa punktów i nie pojawia się w klasyfikacji: partia toczy się między tymi, którzy się chowają, on prowadzi widowisko. I może mylić się dowolną liczbę razy, podczas gdy wszystkie inne tryby kończą się przy pierwszej pomyłce. Transmisja nie może skończyć się na jednym pechowym kliknięciu.',
            'Tryb stream jest darmowy i taki pozostanie.',
          ],
        },
        {
          heading: 'Konkurs, drugi dobry pomysł',
          blocks: [
            'W trybie konkurs wszyscy odmalowują to samo pole tego samego obrazu, a potem każdy głosuje na retusz, który mu się podoba. Nie ma już czego szukać, pozostaje tylko oceniać.',
            'Przed publicznością sprawdza się bardzo dobrze, i to z tego samego powodu co tryb stream: nie ma czego szukać, więc nie ma też czego czat mógłby zdradzić. Propozycje porównuje się obok siebie, głosowanie daje chwilę napięcia, a ujawnienie nazw zapewnia czyste zakończenie. To poza tym najkrótszy tryb.',
          ],
        },
        {
          heading: 'A pozostałe tryby',
          blocks: [
            'Nic nie zmusza do trybu przeznaczonego specjalnie na transmisję, ale wtedy lepiej grać ze znajomymi niż z czatem. We wszystkich pozostałych trybach streamer maluje własne pole na ekranie: widzowie patrzą, jak ono powstaje, i znają odpowiedź, zanim polowanie w ogóle się zacznie.',
            'Partię ze znajomymi ogląda się natomiast bardzo dobrze z zewnątrz: faza rysowania pokazuje, jak każdy buduje swoją kryjówkę, a odsłona ujawnia wszystkie pola naraz, wraz z nazwą autora.',
          ],
        },
        {
          heading: 'Jak wciągnąć czat do gry',
          blocks: [
            'Link z zaproszeniem to jedyna rzecz, którą trzeba udostępnić. Wklej go na czat, a pierwsi przybyli zapełnią stół: żadnego konta do założenia, żadnego pobierania, żadnej kolejki.',
            'Limit graczy ustawia się w pokoju, od 2 do 10. Przy licznej widowni otwórz raczej kilka krótkich partii niż jedną długą: runda trwa krócej niż trzy minuty.',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'Nowości w Retusch',
      date: '2026-08-21',
      summary:
        'Zmiany w rozgrywce Retusch, od najnowszych do najstarszych: nowe tryby, nowe zasady, usprawnienia rysowania i polowania.',
      sections: [
        {
          heading: 'Sierpień 2026',
          blocks: [
            {
              list: [
                'Pole do odmalowania nie zaczyna się już od białego kwadratu, lecz od dominującego koloru tego, co zakrywa. Nie traci się już połowy czasu na zamalowywanie tła, którego w obrazie w ogóle nie ma.',
                'Wiadro z farbą nie zostawia już jasnej obwódki wzdłuż pociągnięć: wsuwa się teraz pod ich zmiękczoną krawędź.',
                'Battle royale nie liczy już punktów. Pierwszy odkryty gracz opuszcza partię, a każdy dostaje miejsce w rundzie pokazane na swoim polu, od czerwonego do zielonego.',
                'Tryb stream daje streamerowi tyle prób, ile zechce: pierwsze chybienie nie kończy już rundy.',
                'W trybie obserwatora można teraz przesuwać i przybliżać obraz, który się właśnie ogląda.',
                'Reakcje emoji wylatują od pseudonimu autora na liście graczy, zamiast unosić się od dołu ekranu.',
                'Poza polem do odmalowania pędzel i wiadro przesuwają widok: nie trzeba już sięgać po rączkę, żeby się przemieścić.',
                'Nazwa gry w lewym górnym rogu podczas partii odsyła na stronę główną po potwierdzeniu.',
                'Japoński i koreański dołączają do piętnastu dostępnych już języków.',
              ],
            },
          ],
        },
        {
          heading: 'Lipiec 2026',
          blocks: [
            {
              list: [
                'Nowy tryb konkurs: wszyscy odmalowują to samo pole, a potem każdy głosuje na retusz, który mu się podoba.',
                'Nowy tryb stream, pomyślany dla transmisji na żywo: gospodarz szuka sam, a jego widownia się chowa.',
                'Kategorie obrazów zastępują poziomy trudności. Zaznacza się te, które chce się widzieć, a każda kategoria pokazuje przykładowy obraz.',
                'Dwie rundy z rzędu nie trafiają już na ten sam obraz, a zaznaczone kategorie zmieniają się z rundy na rundę.',
                'Galeria na koniec partii pozwala obejrzeć retusz pociągnięcie po pociągnięciu i zapisać go jako obrazek lub animowany GIF.',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: 'W skrócie',
    allArticles: 'Wszystkie artykuły',
    theRules: 'Zasady gry',
    theModes: 'Tryby gry',
  },
};
